import { IFullWidthCopyFields } from '@/types/contentful';
import { Asset } from 'contentful';
import Image from 'next/legacy/image';
import React, { useEffect } from 'react';
import { getImageData } from '~/utils/contentful';
import RichText from '../RichText';
import { useInView } from 'react-intersection-observer';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { ParagraphNode } from '../RichText/Nodes';
import { useSprings, animated, easings } from '@react-spring/web';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import { imageLoader } from '../Image';

export default function FullwidthCopy({
  header,
  variant,
  subheaderCopy,
  backgroundColor,
  cornerRounding,
  rebusImages
}: IFullWidthCopyFields) {
  const [springs, api] = useSprings(rebusImages?.length || 0, () => ({
    x: 0, // Start stacked together
    config: {
      tension: 100, // Higher tension for more snap
      friction: 15, // Lower friction for more bounce
      easing: easings.easeOutBack // Adds a bouncy overshoot effect
    }
  }));

  const isMobile = !useMediaQuery('desktop');

  const [ref, inView] = useInView({
    triggerOnce: true, // Ensures the animation only triggers the first time the element comes into view
    threshold: isMobile ? 0.5 : 0.8
  });

  useEffect(() => {
    if (rebusImages?.length && inView) {
      // Add a 1-second delay before triggering the animation
      const timeout = setTimeout(() => {
        api.start((index) => {
          if (index === 0) {
            return { x: -60 }; // First image slides left (with slight overlap)
          } else if (index === rebusImages.length - 1) {
            return { x: 60 }; // Last image slides right (with slight overlap)
          }
          return { x: 0 }; // Middle images stay in place
        });
      }); // 1 second delay

      return () => clearTimeout(timeout);
    }
  }, [rebusImages, api, inView]);

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <ParagraphNode
        node={node}
        className="typography-body-1 text-center !text-[20px] font-[350] !leading-[28px]"
      >
        {children}
      </ParagraphNode>
    )
  };
  // Full Width Content //
  return (
    <div className="mx-6" ref={ref}>
      <div
        className="flex w-full items-center justify-center gap-4 px-6 py-16 tablet:px-4xl"
        style={{
          borderRadius: cornerRounding ? '16px' : '0',
          backgroundColor: backgroundColor
        }}
      >
        {/** Content */}
        <div className="flex flex-col items-center gap-6 self-stretch tablet:max-w-[644px]">
          {/** Rebus images */}
          <div
            className="relative flex items-center"
            style={{
              // Assuming rebusImages is an array, we offset by half the total negative margin.
              // (number of overlaps * overlap amount) / 2
              marginRight:
                rebusImages && rebusImages.length > 0
                  ? `calc(-12px * ${(rebusImages.length - 1) / 2})`
                  : 0
            }}
          >
            {rebusImages?.length &&
              springs?.map((style, index) => (
                <animated.div
                  key={index}
                  className={'size-20'}
                  style={{
                    zIndex: index,
                    position: 'relative',
                    marginLeft: index === 0 ? 0 : '-82px',
                    transform: style.x.to((x) => `translateX(${x}px)`)
                  }}
                >
                  <Image
                    {...getImageData(rebusImages[index] as Asset)}
                    loader={imageLoader}
                    width={80}
                    height={80}
                    className="size-20 rounded-[80px]"
                    style={{
                      borderColor: backgroundColor,
                      backgroundColor: backgroundColor
                    }}
                  />
                </animated.div>
              ))}
          </div>
          <h2
            className={
              variant
                ? 'typography-display-2 self-stretch text-center font-glReproDisplay text-[32px] leading-10 tablet:text-5xl tablet:leading-[3.5rem]'
                : 'typography-display-3 self-stretch text-center font-glReproDisplay text-[28px] leading-9 tablet:text-4xl'
            }
          >
            {header}
          </h2>
          <RichText data={subheaderCopy} overrideOptions={RICH_TEXT_OVERRIDE} />
        </div>
      </div>
    </div>
  );
}
